import React, {
 useState, useRef, useCallback, useEffect
} from 'react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Alert, AlertDescription
} from 'shared/components/ui/alert';
import {
 Upload, ArrowLeft, Loader2, AlertCircle, FileText
} from 'lucide-react';
import {
 parseApiSpec, ParsedSpec
} from '../utils/specParser';

interface ImportSpecUploadProps {
    onBack: () => void;
    onSpecParsed: (spec: ParsedSpec) => void;
}

const loadingMessages = [
    'Analyzing your specification...',
    'Parsing endpoints...',
    'Identifying security schemes...',
    'Extracting connection details...',
];

const ImportSpecUpload: React.FC<ImportSpecUploadProps> = ({
 onBack, onSpecParsed
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Cycle through loading messages
    useEffect(() => {
        if (!isProcessing) return;
        const interval = setInterval(() => {
            setLoadingMsgIndex((prev) => (prev + 1) % loadingMessages.length);
        }, 1500);
        return () => clearInterval(interval);
    }, [isProcessing]);

    const processFile = useCallback(async (file: File) => {
        setError(null);
        setSelectedFile(file);
        setIsProcessing(true);
        setLoadingMsgIndex(0);

        try {
            const content = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('Failed to read file'));
                reader.readAsText(file);
            });

            // Add a minimum delay so the loading overlay is visible
            await new Promise((resolve) => setTimeout(resolve, 3000));

            const result = parseApiSpec(content, file.name);
            onSpecParsed(result);
        } catch (err: any) {
            setError(err.message || 'Failed to parse specification file.');
            setIsProcessing(false);
            setSelectedFile(null);
        }
    }, [onSpecParsed]);

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            processFile(files[0]);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            processFile(files[0]);
        }
    };

    return (
        <>
            <div className="flex flex-col gap-6 max-w-3xl mx-auto py-8">
                <div>
                    <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                    </Button>
                    <h2 className="text-2xl font-bold">Import from API Specification</h2>
                    <p className="text-muted-foreground mt-1">
                        Supports OpenAPI v2/v3 (JSON/YAML) and Postman Collections
                    </p>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Drop zone */}
                <div
                    className={`
                        relative flex flex-col items-center justify-center gap-4
                        rounded-lg border-2 border-dashed p-16 transition-all cursor-pointer
                        ${dragActive
                            ? 'border-primary bg-primary/5 scale-[1.01]'
                            : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50'
                        }
                    `}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json,.yaml,.yml"
                        className="hidden"
                        onChange={handleFileChange}
                    />

                    <div className="rounded-full bg-muted p-6">
                        <Upload className="h-10 w-10 text-muted-foreground" />
                    </div>

                    <div className="text-center">
                        <p className="text-lg font-medium">
                            {dragActive ? 'Drop your file here' : 'Drop your file here, or click to browse'}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                            .json, .yaml, .yml files accepted
                        </p>
                    </div>

                    {selectedFile && !isProcessing && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <FileText className="h-4 w-4" />
                            {selectedFile.name}
                        </div>
                    )}
                </div>
            </div>

            {/* Full-screen loading overlay */}
            {isProcessing && (
                <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background/80 backdrop-blur-sm">
                    <div className="flex flex-col items-center gap-6 p-8">
                        <Loader2 className="h-12 w-12 animate-spin text-primary" />
                        <div className="text-center">
                            <p className="text-lg font-medium animate-pulse">
                                {loadingMessages[loadingMsgIndex]}
                            </p>
                            {selectedFile && (
                                <p className="text-sm text-muted-foreground mt-2">
                                    Processing {selectedFile.name}
                                </p>
                            )}
                        </div>
                        <div className="w-64 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]"
                                style={{
                                    width: '40%',
                                    animation: 'indeterminate 1.5s ease-in-out infinite',
                                }}
                            />
                        </div>
                    </div>
                    <style>{`
                        @keyframes indeterminate {
                            0% { transform: translateX(-100%); }
                            50% { transform: translateX(150%); }
                            100% { transform: translateX(-100%); }
                        }
                    `}</style>
                </div>
            )}
        </>
    );
};

export default ImportSpecUpload;
